import {
  Grid,
  Stack,
  Box,
  Typography,
  FormControl,
  MenuItem,
  LinearProgress,
  CircularProgress,
  Tabs,
  Tab,
  Button,
  ButtonGroup,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo } from "react";
import { FilledButton } from "../../customComponents/styled/styledButtons";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import ModeCommentIcon from "@mui/icons-material/ModeComment";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import {
  StyledSelect,
  StyledTextArea,
  StyledTextField,
} from "../../customComponents/styled/styledInputs";
import { useGetClassesQuery } from "../classroom/classApiSlice";
import {
  useGetCurrentTermQuery,
  useGetSessionQuery,
} from "../settings/session/sessionApiSlice";
import { useGetAssessmentsQuery } from "../settings/assessments/assessmentsApiSlice";
import { useGetSchoolProfileQuery } from "../settings/school/schoolApiSlice";
import toast from "../../../helpers/toast";
import {
  useCreateCommentMutation,
  useCreateRecordMutation,
  useDeleteRecordsBySubjectAndClassMutation,
  useDeleteRecordsByStudentsMutation,
  useGetCommentBySubjectAndClassroomQuery,
  useGetRecordsBySubjectAndClassQuery,
} from "./recordApiSlice";
import { useNetworkStatus } from "../../../hooks/useNetworkStatus";
import CustomWarningDialog from "../../customComponents/CustomWarningDialog";
import { useHotkeys } from "react-hotkeys-hook";
import AICommentModal from "../../customComponents/AICommentModal";
import AIScoreExtractModal from "../../customComponents/AIScoreExtractModal";

const buildRecordKey = (studentId, assessmentId) =>
  `${String(studentId)}:${String(assessmentId)}`;

const buildCommentKey = (studentId) => String(studentId);

const normalizeScore = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const normalizeComment = (comment) => ({
  title: comment?.title || "",
  content: comment?.content || "",
});

const getChangedComments = (currentComments, originalMap) =>
  (currentComments || []).filter((comment) => {
    const key = buildCommentKey(comment.student_id);
    const next = normalizeComment(comment);
    const existing = originalMap.get(key);

    if (!existing) {
      return next.title !== "" || next.content !== "";
    }

    return existing.title !== next.title || existing.content !== next.content;
  });

function ScoreSheet({ subjects, activeSubject, classrooms }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeCommentTab, setActiveCommentTab] = React.useState(0);
  const [scoreSheet, setScoreSheet] = React.useState([]);
  const [commentSheet, setCommentSheet] = React.useState([]);
  const [midtermCommentSheet, setMidtermCommentSheet] = React.useState([]);
  const [openWarningDialog, setOpenWarningDialog] = React.useState(false);
  const [openAiComment, setOpenAiComment] = React.useState(false);
  const [openAiExtract, setOpenAiExtract] = React.useState(false);

  const { data: students, isFetching: isLoadingStudents } = useGetClassesQuery(
    `${subjects[activeSubject]?.classroom_id}/students`,
    { skip: !subjects[activeSubject]?.classroom_id },
  );
  const { data: session } = useGetSessionQuery("current");
  const { data: term } = useGetCurrentTermQuery();
  const activeSubsectionId = subjects[activeSubject]?.subsection_id;
  const { data: assessments } = useGetAssessmentsQuery(
    `all/${activeSubsectionId}`,
    { skip: !activeSubsectionId },
  );
  const { data: schoolInfo } = useGetSchoolProfileQuery();
  const activeSubjectId = subjects[activeSubject]?.id;
  const activeClassroomId = subjects[activeSubject]?.classroom_id;
  const skipSubjectQueries = !activeSubjectId || !activeClassroomId;

  const { data: records, isLoading: isLoadingRecords } =
    useGetRecordsBySubjectAndClassQuery(
      {
        subject_id: activeSubjectId,
        classroom_id: activeClassroomId,
        include_raw: true,
      },
      { skip: skipSubjectQueries },
    );

  const originalScoreMap = useMemo(() => {
    const map = new Map();
    (records || []).forEach((item) => {
      map.set(
        buildRecordKey(
          item.student_id,
          item.assessment_id ?? item.assessment?.id,
        ),
        normalizeScore(item.score),
      );
    });
    return map;
  }, [records]);

  const lockedAssessmentIds = useMemo(() => {
    const set = new Set();
    (records || []).forEach((r) => {
      if (r.raw != null) {
        const assessmentId = r.assessment_id ?? r.assessment?.id;
        if (assessmentId != null) {
          set.add(parseInt(assessmentId));
        }
      }
    });
    return set;
  }, [records]);

  const myAssessments = useMemo(() => assessments || [], [assessments]);

  const allAssessmentsLocked = useMemo(() => {
    if (!myAssessments?.length) return false;
    return myAssessments.every((a) => lockedAssessmentIds.has(parseInt(a.id)));
  }, [myAssessments, lockedAssessmentIds]);

  const { data: comments } = useGetCommentBySubjectAndClassroomQuery(
    {
      subject_id: activeSubjectId,
      classroom_id: activeClassroomId,
      type: "term",
    },
    { skip: skipSubjectQueries },
  );
  const { data: midtermComments } = useGetCommentBySubjectAndClassroomQuery(
    {
      subject_id: activeSubjectId,
      classroom_id: activeClassroomId,
      type: "midterm",
    },
    { skip: skipSubjectQueries },
  );

  const originalCommentMap = useMemo(() => {
    const map = new Map();
    (comments || []).forEach((item) => {
      map.set(buildCommentKey(item.student_id), normalizeComment(item));
    });
    return map;
  }, [comments]);

  const originalMidtermCommentMap = useMemo(() => {
    const map = new Map();
    (midtermComments || []).forEach((item) => {
      map.set(buildCommentKey(item.student_id), normalizeComment(item));
    });
    return map;
  }, [midtermComments]);

  const { isOnline } = useNetworkStatus();

  const [createRecord, { isLoading: createRecordLoading }] =
    useCreateRecordMutation();
  const [createComment, { isLoading: createCommentLoading }] =
    useCreateCommentMutation();
  const [deleteRecord, { isLoading: deleteRecordLoading }] =
    useDeleteRecordsBySubjectAndClassMutation();
  const [deleteRecordsByStudents] = useDeleteRecordsByStudentsMutation();

  const isCommentBased = subjects[activeSubject]?.report_type === "skill";
  const [offeringStatus, setOfferingStatus] = React.useState([]);

  // Clear sheets immediately when the active subject changes.
  // This prevents stale data from a previous subject being displayed or
  // accidentally saved for the newly selected subject (e.g. when offline
  // and the new subject has no cached records, the populate-effect below
  // is skipped because `records` stays undefined, leaving the old data in state).
  useEffect(() => {
    setScoreSheet([]);
    setCommentSheet([]);
    setMidtermCommentSheet([]);
  }, [activeSubject]);

  // Initialize offering status when students/records are loaded.
  // If nobody in the class has a saved score yet, default everyone to
  // offering. Once at least one student has a score, anyone without one
  // is treated as not offering (their records were cleared, or they were
  // never scored because they don't take this subject).
  useEffect(() => {
    if (students) {
      const studentIdsWithRecords = new Set(
        (records || []).map((record) => String(record.student_id)),
      );
      const anyoneHasRecords = studentIdsWithRecords.size > 0;
      setOfferingStatus(
        students.map((student) => ({
          student_id: student.id,
          offering:
            !anyoneHasRecords || studentIdsWithRecords.has(String(student.id))
              ? 1
              : 2,
        })),
      );
    }
  }, [students, records]);

  // Handle the offering status change for a specific student
  const handleOfferingChange = (event, studentId) => {
    const value = event.target.value;
    setOfferingStatus((prevStatus) =>
      prevStatus.map((status) =>
        status.student_id === studentId
          ? { ...status, offering: value }
          : status,
      ),
    );
  };
  const handleMarkAllOffering = (value) => {
    setOfferingStatus((prevStatus) =>
      prevStatus.map((status) => ({ ...status, offering: value })),
    );
  };

  useEffect(() => {
    //set score sheet to records gotten from db
    if (records) {
      setScoreSheet(
        records?.map((item) => ({
          student_id: item.student_id.toString(),
          score: item.score,
          assessment_id: item.assessment.id.toString(),
        })),
      );
    }

    if (comments) {
      setCommentSheet(
        comments?.map((item) => ({
          student_id: item.student_id,
          title: item.title || "",
          content: item.content || "",
        })),
      );
    }

    if (midtermComments) {
      setMidtermCommentSheet(
        midtermComments?.map((item) => ({
          student_id: item.student_id,
          title: item.title || "",
          content: item.content || "",
        })),
      );
    } else {
      setMidtermCommentSheet([]);
    }
  }, [records, comments, midtermComments]);

  const studentTotal = (studentId) => {
    const scores = scoreSheet?.filter(
      (item) => parseInt(item.student_id) === parseInt(studentId),
    );
    if (!scores || scores.length === 0) return "";
    return scores.reduce((sum, item) => {
      const score = parseFloat(item.score);
      return sum + (isNaN(score) ? 0 : score);
    }, 0);
  };

  const handleChange = (event) => {
    let { name: assessment, id: student, value } = event.target;

    //get the obtainable score for validation
    const obtainable = myAssessments?.find(
      (item) => parseInt(item.id) === parseInt(assessment),
    ).obtainable_score;

    if (value !== "" && value > obtainable) {
      // validation
      toast.error("Score cannot be greater than obtainable score");
      value = obtainable;
    }

    // insert or update existing score sheet entry
    setScoreSheet((prevState) => {
      const newScoreSheet = prevState
        ?.map((item) => {
          if (
            item.student_id === parseInt(student) &&
            item.assessment_id === assessment
          ) {
            return { ...item, score: value };
          } else {
            return item;
          }
        })
        .filter(
          (item) =>
            item.student_id !== student || item.assessment_id !== assessment,
        );
      return [
        ...newScoreSheet,
        {
          student_id: student,
          score: typeof value === "string" ? parseFloat(value) : value,
          assessment_id: assessment,
        },
      ];
    });
  };

  const handleCommentChange = (event) => {
    let { id: student, value } = event.target;
    setCommentSheet((prevState) => {
      const newCommentSheet = prevState
        .map((item) => {
          if (item.student_id === parseInt(student)) {
            return { ...item, content: value };
          } else {
            return item;
          }
        })
        .filter((item) => item.student_id !== parseInt(student));
      return [
        ...newCommentSheet,
        {
          student_id: parseInt(student),
          title: "",
          content: value,
        },
      ];
    });
  };

  const handleMidtermCommentChange = (event) => {
    let { id: student, value } = event.target;
    setMidtermCommentSheet((prevState) => {
      const newSheet = prevState
        .map((item) => {
          if (item.student_id === parseInt(student)) {
            return { ...item, content: value };
          } else {
            return item;
          }
        })
        .filter((item) => item.student_id !== parseInt(student));
      return [
        ...newSheet,
        {
          student_id: parseInt(student),
          title: "",
          content: value,
        },
      ];
    });
  };

  const handleSave = useCallback(async () => {
    const notOfferingStudentIds = offeringStatus
      .filter((s) => s.offering === 2)
      .map((s) => s.student_id);

    let hasDeletedNotOffering = false;
    if (notOfferingStudentIds.length > 0) {
      try {
        await deleteRecordsByStudents({
          subject_id: subjects[activeSubject]?.id,
          classroom_id: subjects[activeSubject]?.classroom_id,
          student_ids: notOfferingStudentIds,
          type: activeCommentTab === 1 ? "midterm" : "term",
        }).unwrap();
        hasDeletedNotOffering = true;
        setScoreSheet((prev) =>
          prev.filter(
            (item) =>
              !notOfferingStudentIds.includes(parseInt(item.student_id)),
          ),
        );
        setCommentSheet((prev) =>
          prev.filter(
            (item) => !notOfferingStudentIds.includes(item.student_id),
          ),
        );
        setMidtermCommentSheet((prev) =>
          prev.filter(
            (item) => !notOfferingStudentIds.includes(item.student_id),
          ),
        );
      } catch (error) {
        toast.error("Error clearing records for not-offering students");
      }
    }

    if (activeCommentTab === 1) {
      const changedMidtermComments = getChangedComments(
        midtermCommentSheet.filter(
          (c) => !notOfferingStudentIds.includes(c.student_id),
        ),
        originalMidtermCommentMap,
      );

      if (changedMidtermComments.length === 0) {
        if (hasDeletedNotOffering) {
          toast.success("Not-offering student records cleared");
        } else {
          toast.info("No midterm comment changes to save");
        }
        return;
      }

      // Midterm tab: save only midterm comments
      const midtermCommentBody = {
        subject_id: subjects[activeSubject]?.id,
        classroom_id: subjects[activeSubject]?.classroom_id,
        type: "midterm",
        comments: changedMidtermComments,
      };
      try {
        const response = await createComment(midtermCommentBody).unwrap();
        if (response?.queued) {
          toast.info("Queued (offline) — will sync when you reconnect");
        } else if (response) {
          toast.success("Midterm comments saved");
        }
      } catch (error) {
        toast.error(error?.message || "Error saving midterm comments");
      }
      return;
    }

    // Term tab: for comment-based classes save only comments, otherwise save records + comments
    const commentBody = {
      subject_id: subjects[activeSubject]?.id,
      classroom_id: subjects[activeSubject]?.classroom_id,
      comments: getChangedComments(
        commentSheet.filter(
          (c) => !notOfferingStudentIds.includes(c.student_id),
        ),
        originalCommentMap,
      ),
    };

    const hasChangedComments = commentBody.comments.length > 0;

    if (isCommentBased) {
      if (!hasChangedComments) {
        if (hasDeletedNotOffering) {
          toast.success("Not-offering student comments cleared");
        } else {
          toast.info("No comment changes to save");
        }
        return;
      }

      try {
        const response = await createComment(commentBody).unwrap();
        if (response?.queued) {
          toast.info("Queued (offline) — will sync when you reconnect");
        } else if (response) {
          toast.success("Comments saved");
        }
      } catch (error) {
        toast.error(error?.message || "Error saving comments");
      }
      return;
    }

    // Convert empty scores to 0 before saving, but skip students marked as "not offering"
    const processedRecords = scoreSheet
      .filter((r) => !notOfferingStudentIds.includes(parseInt(r.student_id)))
      .map((r) => ({
        ...r,
        score:
          r.score === "" || r.score === undefined ? 0 : normalizeScore(r.score),
      }));

    const changedRecords = processedRecords.filter((record) => {
      const key = buildRecordKey(record.student_id, record.assessment_id);
      const originalScore = originalScoreMap.get(key);
      if (originalScore === undefined) {
        return true;
      }
      return normalizeScore(record.score) !== normalizeScore(originalScore);
    });

    if (changedRecords.length === 0 && !hasChangedComments) {
      if (hasDeletedNotOffering) {
        toast.success("Not-offering student records cleared successfully");
      } else {
        toast.info("No score or comment changes to save");
      }
      return;
    }

    if (changedRecords.length === 0 && hasChangedComments) {
      try {
        const createCommentResponse = await createComment(commentBody).unwrap();
        if (createCommentResponse?.queued) {
          toast.info(
            "Queued (offline) — comments will sync when you reconnect",
          );
        } else if (createCommentResponse) {
          toast.success(
            hasDeletedNotOffering
              ? "Not-offering records cleared & comments saved"
              : "No score changes to save. Comments saved",
          );
        } else {
          toast.error("Error Saving Comment");
        }
      } catch (error) {
        toast.error(error?.message || "Error saving comments");
      }
      return;
    }

    const recordBody = {
      subject_id: subjects[activeSubject]?.id,
      classroom_id: subjects[activeSubject]?.classroom_id,
      records: changedRecords,
    };
    try {
      const response = await createRecord(recordBody).unwrap();
      if (response?.queued) {
        toast.info("Queued (offline) — will sync when you reconnect");
      } else if (response) {
        if (!hasChangedComments) {
          toast.success(
            hasDeletedNotOffering
              ? "Not-offering records cleared & score sheet saved successfully"
              : "Score sheet saved successfully",
          );
          return;
        }

        const createCommentResponse = await createComment(commentBody).unwrap();
        if (createCommentResponse?.queued) {
          toast.info(
            "Queued (offline) — comments will sync when you reconnect",
          );
        } else if (createCommentResponse) {
          toast.success("Comment saved");
        } else {
          toast.error("Error Saving Comment");
        }
      }
    } catch (error) {
      toast.error(error?.message || "Error saving score sheet");
    }
  }, [
    activeCommentTab,
    activeSubject,
    commentSheet,
    createComment,
    createRecord,
    deleteRecordsByStudents,
    isCommentBased,
    midtermCommentSheet,
    offeringStatus,
    originalCommentMap,
    originalMidtermCommentMap,
    originalScoreMap,
    scoreSheet,
    subjects,
  ]);

  const handleDelete = async () => {
    try {
      const response = await deleteRecord({
        classId: subjects[activeSubject]?.classroom_id,
        subjectId: subjects[activeSubject]?.id,
      }).unwrap();
      if (response) {
        toast.success(response.message);
        setScoreSheet([]);
      }
    } catch (error) {
      toast.error("Error deleting score sheet");
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey && e.key === "s") || (e.metaKey && e.key === "s")) {
        e.preventDefault();
        handleSave();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleSave]);

  return (
    <Grid container columnSpacing={3}>
      {/* delete warning */}
      <CustomWarningDialog
        open={openWarningDialog}
        setOpen={setOpenWarningDialog}
        action={handleDelete}
        message="!!!WARNING!!! Irreversable Action!! are sure you want to clear these record?"
      />
      <Grid item xs={12} lg={11}>
        <StyledPaper>
          {isLoadingStudents && <LinearProgress />}
          {isLoadingRecords && <LinearProgress />}
          <Box
            className="paperBody"
            sx={{ textAlign: "center", pb: isMobile ? 10 : 0 }}
          >
            <Typography variant="formHeading" color="primary.main">
              {schoolInfo?.name}
            </Typography>
            <Typography variant="formSubHeading">
              <b>Session:</b>
              {session?.name}|<b>Term:</b> {term?.name} |<b>Class:</b>
              {
                classrooms?.find(
                  (item) => item.id === subjects[activeSubject]?.classroom_id,
                )?.name
              }
              |<b>Subject:</b> {subjects[activeSubject]?.name}
            </Typography>

            {lockedAssessmentIds.size > 0 && (
              <Typography
                variant="body2"
                sx={{ color: "warning.main", fontWeight: 600, mb: 1 }}
              >
                {allAssessmentsLocked
                  ? "Scores are managed by CA Manager / Quiz / CBT — score fields are locked. Comments can still be entered and saved."
                  : "Some assessment fields (e.g. CBT/Quiz) are managed automatically and locked. Unlocked score fields and comments can still be edited and saved."}
              </Typography>
            )}

            {/* Term / Midterm tabs */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={2}
              sx={{ mb: 2 }}
            >
              <Tabs
                value={activeCommentTab}
                onChange={(_, val) => setActiveCommentTab(val)}
              >
                <Tab label="Term" />
                <Tab label="Midterm" />
              </Tabs>
            </Stack>

            {/* ── Term tab ── */}
            {activeCommentTab === 0 && (
              <Grid container spacing={2}>
                {!isCommentBased && students?.length > 0 && (
                  <Grid item xs={12}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Mark all as:
                      </Typography>
                      <ButtonGroup size="small" variant="outlined">
                        <Button onClick={() => handleMarkAllOffering(1)}>
                          Offering
                        </Button>
                        <Button onClick={() => handleMarkAllOffering(2)}>
                          Not Offering
                        </Button>
                      </ButtonGroup>
                    </Stack>
                  </Grid>
                )}
                {students?.map((student) => {
                  const isNotOffering =
                    offeringStatus.find(
                      (status) => status.student_id === student.id,
                    )?.offering === 2;
                  return (
                    <React.Fragment key={student.id}>
                      {!isCommentBased && (
                        <Grid item xs={12} lg={3}>
                          <FormControl fullWidth>
                            <StyledSelect
                              required
                              id="offering"
                              name="offering"
                              value={
                                offeringStatus.find(
                                  (status) => status.student_id === student.id,
                                )?.offering || 1
                              }
                              onChange={(event) =>
                                handleOfferingChange(event, student.id)
                              }
                              displayEmpty
                            >
                              <MenuItem value={1}>offering</MenuItem>
                              <MenuItem value={2}>not offering</MenuItem>
                            </StyledSelect>
                          </FormControl>
                        </Grid>
                      )}
                      <Grid item xs={12} lg={isCommentBased ? 8 : 5}>
                        <FormControl fullWidth>
                          <StyledTextField
                            required
                            value={student.first_name + " " + student.last_name}
                            disabled
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} lg={4}>
                        <FormControl fullWidth>
                          <StyledTextField
                            required
                            disabled
                            value={student.reg_number}
                          />
                        </FormControl>
                      </Grid>
                      {isNotOffering ? (
                        <Grid item xs={12}>
                          <Box
                            sx={{
                              py: 1,
                              px: 2,
                              bgcolor: "action.hover",
                              borderRadius: 1,
                              textAlign: "center",
                              color: "text.secondary",
                              fontStyle: "italic",
                              fontSize: "0.875rem",
                            }}
                          >
                            Student is not offering this subject
                          </Box>
                        </Grid>
                      ) : (
                        <>
                          {!isCommentBased && (
                            <Grid
                              item
                              xs={12}
                              sx={{
                                display: "flex",
                                alignItems: "flex-end",
                                columnGap: "4px",
                              }}
                            >
                              {myAssessments?.map((assessment) => (
                                <FormControl fullWidth key={assessment.id}>
                                  <Typography variant="formSubHeading">
                                    {assessment.name +
                                      `(${assessment.obtainable_score})`}
                                  </Typography>
                                  <StyledTextField
                                    required
                                    type="number"
                                    id={student.id}
                                    name={assessment.id}
                                    inputProps={{ step: "any" }}
                                    value={
                                      scoreSheet?.find(
                                        (item) =>
                                          parseInt(item.student_id) ===
                                            parseInt(student.id) &&
                                          parseInt(item.assessment_id) ===
                                            parseInt(assessment.id),
                                      )?.score ?? ""
                                    }
                                    onChange={handleChange}
                                    disabled={lockedAssessmentIds.has(
                                      parseInt(assessment.id),
                                    )}
                                  />
                                </FormControl>
                              ))}
                              {myAssessments?.length > 0 && (
                                <FormControl fullWidth>
                                  <Typography variant="formSubHeading">
                                    Total
                                  </Typography>
                                  <StyledTextField
                                    required
                                    type="number"
                                    id={"total"}
                                    name={"total"}
                                    inputProps={{ step: "any" }}
                                    value={studentTotal(student.id)}
                                    onChange={handleChange}
                                    disabled
                                  />
                                </FormControl>
                              )}
                            </Grid>
                          )}
                          <Grid item xs={12}>
                            <FormControl fullWidth>
                              <StyledTextArea
                                id={student.id}
                                name="comment"
                                onChange={handleCommentChange}
                                value={
                                  commentSheet?.find(
                                    (item) =>
                                      parseInt(item.student_id) ===
                                      parseInt(student.id),
                                  )?.content || ""
                                }
                                minRows={2}
                              />
                            </FormControl>
                          </Grid>
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </Grid>
            )}

            {/* ── Midterm tab ── */}
            {activeCommentTab === 1 && (
              <Grid container spacing={2}>
                {students?.map((student) => (
                  <>
                    <Grid item xs={12} lg={8}>
                      <FormControl fullWidth>
                        <StyledTextField
                          required
                          value={student.first_name + " " + student.last_name}
                          disabled
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} lg={4}>
                      <FormControl fullWidth>
                        <StyledTextField
                          required
                          disabled
                          value={student.reg_number}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <StyledTextArea
                          id={student.id}
                          name="midterm_comment"
                          placeholder="Midterm comment"
                          onChange={handleMidtermCommentChange}
                          value={
                            midtermCommentSheet?.find(
                              (item) =>
                                parseInt(item.student_id) ===
                                parseInt(student.id),
                            )?.content || ""
                          }
                          minRows={2}
                        />
                      </FormControl>
                    </Grid>
                  </>
                ))}
              </Grid>
            )}
          </Box>
        </StyledPaper>
      </Grid>
      {!isMobile && (
        <Grid item xs={12} lg={1}>
          <Stack
            direction={{ xs: "row", md: "column" }}
            spacing={1}
            sx={{
              "& .btn-icon": {
                fontSize: "14px",
              },
            }}
          >
            {createRecordLoading || createCommentLoading ? (
              <CircularProgress size={28} />
            ) : (
              <Tooltip
                title={
                  !isOnline
                    ? "Offline — changes will be queued and synced when you reconnect"
                    : ""
                }
                placement="left"
              >
                <FilledButton
                  className="btn-icon"
                  onClick={handleSave}
                  sx={
                    !isOnline
                      ? { opacity: 0.75, border: "2px solid #ff9800" }
                      : {}
                  }
                >
                  <SaveIcon />
                </FilledButton>
              </Tooltip>
            )}
            {!allAssessmentsLocked &&
              activeCommentTab === 0 &&
              (deleteRecordLoading ? (
                <CircularProgress size={28} />
              ) : (
                <FilledButton
                  className="btn-icon"
                  onClick={() => setOpenWarningDialog(true)}
                >
                  <DeleteIcon />
                </FilledButton>
              ))}
            {
              <Tooltip placement="left" title="Generate AI comments">
                <FilledButton
                  className="btn-icon"
                  onClick={() => setOpenAiComment(true)}
                >
                  <ModeCommentIcon />
                </FilledButton>
              </Tooltip>
            }
            {!allAssessmentsLocked && activeCommentTab === 0 && (
              <Tooltip
                placement="left"
                title="Extract scores from image/doc/audio"
              >
                <FilledButton
                  className="btn-icon"
                  onClick={() => setOpenAiExtract(true)}
                >
                  <DocumentScannerIcon />
                </FilledButton>
              </Tooltip>
            )}
          </Stack>
        </Grid>
      )}

      {isMobile && (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: (zIndex) => zIndex.appBar,
            backgroundColor: "background.paper",
            borderTop: "1px solid",
            borderColor: "divider",
            px: 2,
            py: 1,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
            sx={{
              "& .btn-icon": {
                fontSize: "14px",
              },
            }}
          >
            {createRecordLoading || createCommentLoading ? (
              <CircularProgress size={28} />
            ) : (
              <Tooltip
                title={
                  !isOnline
                    ? "Offline — changes will be queued and synced when you reconnect"
                    : ""
                }
              >
                <FilledButton
                  className="btn-icon"
                  onClick={handleSave}
                  sx={
                    !isOnline
                      ? { opacity: 0.75, border: "2px solid #ff9800" }
                      : {}
                  }
                >
                  <SaveIcon />
                </FilledButton>
              </Tooltip>
            )}
            {!allAssessmentsLocked &&
              activeCommentTab === 0 &&
              (deleteRecordLoading ? (
                <CircularProgress size={28} />
              ) : (
                <FilledButton
                  className="btn-icon"
                  onClick={() => setOpenWarningDialog(true)}
                >
                  <DeleteIcon />
                </FilledButton>
              ))}
            {
              <Tooltip title="Generate AI comments">
                <FilledButton
                  className="btn-icon"
                  onClick={() => setOpenAiComment(true)}
                >
                  <ModeCommentIcon />
                </FilledButton>
              </Tooltip>
            }
            {!allAssessmentsLocked && activeCommentTab === 0 && (
              <Tooltip title="Extract scores from image/doc/audio">
                <FilledButton
                  className="btn-icon"
                  onClick={() => setOpenAiExtract(true)}
                >
                  <DocumentScannerIcon />
                </FilledButton>
              </Tooltip>
            )}
          </Stack>
        </Box>
      )}

      {/* AI Score Extract Modal — quick entry multi-assessment mode */}
      <AIScoreExtractModal
        open={openAiExtract}
        onClose={() => setOpenAiExtract(false)}
        subjectId={activeSubjectId}
        classroomId={activeClassroomId}
        termId={term?.id || null}
        assessmentId={null}
        assessments={myAssessments || []}
        students={students || []}
        onApply={(extracted) => {
          setScoreSheet((prev) => {
            const updated = [...prev];
            for (const item of extracted) {
              const obtainable =
                myAssessments?.find((a) => a.id === item.assessment_id)
                  ?.obtainable_score ?? Infinity;
              const score = Math.min(item.score ?? 0, obtainable);
              const idx = updated.findIndex(
                (r) =>
                  String(r.student_id) === String(item.student_id) &&
                  String(r.assessment_id) === String(item.assessment_id),
              );
              const entry = {
                student_id: String(item.student_id),
                assessment_id: String(item.assessment_id),
                score,
              };
              if (idx >= 0) updated[idx] = entry;
              else updated.push(entry);
            }
            return updated;
          });
        }}
      />

      {/* AI Comment Modal — subject teacher batch generation */}
      <AICommentModal
        open={openAiComment}
        onClose={() => setOpenAiComment(false)}
        roleType="subject_teacher"
        subjectId={activeSubjectId}
        classroomId={activeClassroomId}
        commentType={activeCommentTab === 1 ? "midterm" : "term"}
        commentTitle=""
        termId={term?.id || null}
        students={students || []}
        existingComments={(activeCommentTab === 1
          ? midtermCommentSheet
          : commentSheet
        ).map((c) => ({
          student_id: c.student_id,
          content: c.content,
        }))}
        onConfirmed={() => setOpenAiComment(false)}
      />
    </Grid>
  );
}

export default ScoreSheet;
